import React from 'react'

import { Box, Flex, Link, Text } from '@driftt/dds'
import { CircleLoader, Div, H6 } from '@driftt/tide-core'
import { Icon } from '@driftt/tide-icons'

import { DeserializedSavedFilter } from 'api/AccountActivityAPI/types'
import SavedFiltersActionMenu from 'components/SavedFiltersActionMenu'
import { isEU } from 'config'
import { INACTIVE_FILTER } from 'hooks/useSavedFilters'

import styles from './styles.module.css'

interface SavedViewsSidebarProps {
  filtersLoading: boolean
  sharedFilters: DeserializedSavedFilter[]
  privateFilters: DeserializedSavedFilter[]
  activeFilterId?: number
  defaultFilterId?: number
  setActiveFilterId: (id: number | undefined) => void
  updateDefaultFilterId: (id: number) => void
  removeSavedFilter: (id: number) => void
  onOpenEditModal: () => void
  onCreateSavedFilter: () => void
  onShowFeedbackModal: () => void
}

const SavedViewsSidebar = ({
  filtersLoading,
  activeFilterId,
  defaultFilterId,
  setActiveFilterId,
  updateDefaultFilterId,
  removeSavedFilter,
  onOpenEditModal,
  onCreateSavedFilter,
  onShowFeedbackModal,
  sharedFilters,
  privateFilters
}: SavedViewsSidebarProps) => {
  const highlightAllAccounts = activeFilterId === INACTIVE_FILTER

  if (filtersLoading) {
    return (
      <Div className={styles.SavedFiltersLoading}>
        <CircleLoader />
      </Div>
    )
  }

  const showToolTip = ({ name }: DeserializedSavedFilter): boolean => name.length >= 17

  return (
    <Div>
      <Div className={styles.SidebarTopSection}>
        <H6 className={styles.ActivitiesViewItemAccounts}>Accounts</H6>
        <Box
          css={{
            backgroundColor: highlightAllAccounts ? '$gray2' : '$white',
            padding: '$1',
            borderRadius: '$1',
            cursor: 'pointer',
            margin: '5px 0',
            '&:hover': {
              backgroundColor: '$gray2'
            }
          }}
          onClick={() => setActiveFilterId(undefined)}>
          <Text variant="bodyBold">All accounts</Text>
        </Box>
        {!isEU && (
          <Link
            external
            css={{
              '&:hover': {
                textDecoration: 'none'
              }
            }}
            variant="subtle"
            target="_blank"
            href="/live">
            <Flex
              alignItems="center"
              css={{
                backgroundColor: '$white',
                padding: '$1',
                borderRadius: '$1',
                cursor: 'pointer',
                margin: '5px 0',
                '&:hover': {
                  backgroundColor: '$gray2'
                }
              }}>
              <Text variant="bodyBold">Live view</Text>
              <Icon height={16} width={16} name="open-new-tab" />
            </Flex>
          </Link>
        )}
        {(!privateFilters || privateFilters.length > 0) && (
          <React.Fragment>
            <H6 className={styles.ActivitySectionSavedFilters}>Your Saved Filters</H6>
            {privateFilters.map((filter: DeserializedSavedFilter) => (
              <SavedFiltersActionMenu
                key={filter.id}
                savedFilter={filter}
                isActive={activeFilterId === filter.id}
                defaultFilterId={defaultFilterId}
                onOpenEditModal={onOpenEditModal}
                updateDefaultFilterId={updateDefaultFilterId}
                removeSavedFilter={removeSavedFilter}
                setActiveFilterId={setActiveFilterId}
                showToolTip={showToolTip(filter)}
              />
            ))}
          </React.Fragment>
        )}
        {(!sharedFilters || sharedFilters.length > 0) && (
          <React.Fragment>
            <H6 className={styles.ActivitySectionSavedFilters}>Shared Saved Filters</H6>
            {sharedFilters.map((filter: DeserializedSavedFilter) => (
              <SavedFiltersActionMenu
                key={filter.id}
                savedFilter={filter}
                isActive={activeFilterId === filter.id}
                defaultFilterId={defaultFilterId}
                onOpenEditModal={onOpenEditModal}
                updateDefaultFilterId={updateDefaultFilterId}
                removeSavedFilter={removeSavedFilter}
                setActiveFilterId={setActiveFilterId}
                showToolTip={showToolTip(filter)}
              />
            ))}
          </React.Fragment>
        )}

        <hr className={styles.Separator} />
        <Flex
          alignItems="center"
          css={{
            backgroundColor: '$white',
            padding: '$1',
            borderRadius: '$1',
            cursor: 'pointer',
            margin: '5px 0',
            '&:hover': {
              backgroundColor: '$gray2'
            }
          }}
          fullWidth
          onClick={onCreateSavedFilter}>
          <Text variant="bodyBold">Create saved filter</Text>
          <Icon name="plus" height={12} width={12} />
        </Flex>
      </Div>

      <Flex
        gap="half"
        alignItems="center"
        css={{
          backgroundColor: '$white',
          padding: '$1 0',
          borderRadius: '$1',
          cursor: 'pointer',
          margin: '5px 0',

          '&:hover': {
            backgroundColor: '$gray2'
          }
        }}
        fullWidth
        onClick={onShowFeedbackModal}>
        <Icon name="megaphone-2" height={14} width={16} />
        <Text variant="bodyBold">Give product feedback</Text>
      </Flex>
    </Div>
  )
}

export default SavedViewsSidebar
